import * as React from "react";
import { usePeopleDirectory } from "../../contexts/PeopleDirectoryContext";
import { getInitials, photoUrlOrNull } from "../../utils/people/peopleDirectory";

export type PersonAvatarProps = {
  /** HRMS user id — the directory key. Preferred. */
  userId?: string | null;
  /** HRMS Employee.id (emptID). Only used when there is no userId. */
  employeeId?: string | null;
  /** Employee code such as "DEV009A". Only used when there is no userId. */
  employeeCode?: string | null;
  name?: string | null;
  /** A photo the row already carries — used as-is, no lookup needed. */
  photoUrl?: string | null;
  /** Pixel diameter. Defaults to 32. */
  size?: number;
  square?: boolean;
  className?: string;
  title?: string;
};

/**
 * A person's profile photo, found from whatever id the row has; initials
 * otherwise. The same picture they set in the messenger or on the phone.
 *
 * Mirrors the messenger's components/people/PersonAvatar so a person looks the
 * same in both apps.
 */
export default function PersonAvatar({
  userId, employeeId, employeeCode, name, photoUrl, size = 32, square = false, className, title,
}: PersonAvatarProps) {
  const directory = usePeopleDirectory();
  const { requestEmployees } = directory;

  const ownPhoto = photoUrlOrNull(photoUrl);
  const hasUser = !!userId?.toString().trim();
  const hasHint = !!(employeeId?.toString().trim() || employeeCode?.toString().trim());

  // Only a row that would otherwise be initials pays for the directory, so a
  // list whose rows carry their own photo costs no extra request.
  const needsDirectory = !ownPhoto && (hasUser || hasHint);
  React.useEffect(() => {
    if (needsDirectory) requestEmployees();
  }, [needsDirectory, requestEmployees]);

  const bridged = !ownPhoto && !hasUser && hasHint
    ? directory.employees.resolve({ employeeId, employeeCode })
    : null;
  // A row with a blank userId still gets the bridged user's photo: a plain
  // `userId ?? bridged?.userId` would keep the empty string and skip it.
  const effectiveUserId = hasUser ? userId : bridged?.userId ?? null;
  const uri =
    ownPhoto
    ?? directory.photoForUser(effectiveUserId)
    ?? photoUrlOrNull(bridged?.photoUrl)
    ?? null;

  const [imgError, setImgError] = React.useState(false);
  React.useEffect(() => { setImgError(false); }, [uri]);
  const showImg = !!uri && !imgError;

  const displayName = name || "?";
  const px = size;
  const radius = square ? Math.round(px * 0.28) : 9999;
  const fontPx = px <= 24 ? 10 : px <= 36 ? 12 : Math.round(px * 0.4);

  return (
    <span
      className={`relative inline-flex flex-shrink-0 items-center justify-center overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 shadow-sm ${className || ""}`}
      style={{ width: px, height: px, borderRadius: radius }}
      title={title ?? (name ? displayName : undefined)}
    >
      {/* Initials are the floor, always rendered. The photo covers them, and
          uncovers them again if the image 404s (a deleted blob, a stale URL). */}
      <span className="font-semibold text-gray-700 select-none" style={{ fontSize: fontPx }}>
        {getInitials(displayName)}
      </span>
      {showImg && (
        <img
          src={uri as string}
          alt=""
          loading="lazy"
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ borderRadius: radius }}
          onError={() => setImgError(true)}
        />
      )}
    </span>
  );
}
